import React, { useContext } from "react";
import PropTypes from "prop-types";

import Card from "./Card";
import Button from "./Button";
import RangeSlider from "./RangeSlider";
import RadioButton from "./RadioButton";
import CheckBox from "./CheckBox";

import { WidgetContext } from "./WidgetContext";
import { useBreakpoint } from "./WidgetBreakpoint";

import icon from "./img/icon.svg";
import { Colours } from "@learningblocks/shared-components";
import close from "./img/close.svg";
import play_icon from "./img/play-icon.svg";
import pause_icon from "./img/pause-icon.svg";

import total_legend from "./img/total-legend.svg";
import desired_legend from "./img/desired-legend.svg";
import overhang_legend from "./img/overhang-legend.svg";

export default function WidgetControls(props) {
  // Include the widget providers
  const breakpoints = useBreakpoint();
  const widget = useContext(WidgetContext);

  const updateAxis = (event) => {
    // Toggle the Axis checkbox
    switch (event.target.value) {
      case "fluidXAxis":
        // X Axis
        widget.setFluidXAxis(!widget.fluidXAxis);
        break;
      case "fluidYAxis":
        // Y Axis
        widget.setFluidYAxis(!widget.fluidYAxis);
        break;
      default:
    }
  };

  const updateChartType = (event) => {
    // Toggle the line display
    switch (event.target.value) {
      case "totalDNAline":
        // Total DNA (yellow)
        widget.setTotalDNAline(!widget.totalDNAline);
        break;
      case "desiredRegionLine":
        // Desired region (red)
        widget.setDesiredRegionLine(!widget.desiredRegionLine);
        break;
      case "overhangLine":
        // Desired region (red)
        widget.setOverhangLine(!widget.overhangLine);
        break;
      default:
    }
  };

  return (
    <div
      style={{
        display: !widget.navigationExpanded && breakpoints.md ? "none" : null,
        position: breakpoints.md ? "absolute" : null,
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        top: 0,
        left: 0,
        width: breakpoints.md ? "100%" : null,
        height: "100vh",
        background: "rgba(0,0,0,0.8)",
      }}
    >
      <div
        style={{
          width: breakpoints.md ? "calc(100% - 32px)" : "383px",
          maxWidth: "383px",
          height: "100%",
          flex: "1 0 auto",
          display: "flex",
          flexDirection: "column",
          position: breakpoints.md ? "absolute" : null,
          right: breakpoints.md ? 0 : null,
        }}
      >
        <div
          style={{
            position: "relative",
            flex: "1 0 auto",
            height: "64px",
            width: "auto",
            background: "#0063C3",
            padding: "18px 18px 18px 60px",
            display: breakpoints.md ? "none" : null,
          }}
        >
          <img
            src={icon}
            style={{ position: "absolute", left: "-50px", top: "-5px" }}
            alt="Interactive infographic icon."
          />
          <p
            style={{
              textTransform: "uppercase",
              margin: 0,
              fontWeight: "bold",
              color: "#CCD8E1",
              fontSize: "14px",
            }}
          >
            Interactive Infographic
          </p>
          <h4
            style={{
              margin: 0,
              color: "white",
              fontSize: "20px",
            }}
          >
            One DNA copy to a billion - the amplifying power of PCR.
          </h4>
        </div>
        <div
          className="widgetControls"
          style={{
            width: "auto",
            height: "100%",
            overflowY: "scroll",
            background: "white",
            padding: "0 16px 16px 16px",
          }}
        >
          <div
            style={{
              flexDirection: "column",
              display: !breakpoints.md ? "none" : "flex",
            }}
          >
            <div style={{ alignSelf: "flex-end", marginRight: "-16px" }}>
              <Button
                type="Close"
                onClick={() => widget.setNavigationExpanded(false)}
              >
                <p
                  style={{
                    color: Colours.navy1,
                    margin: "1px 0 0",
                    paddingRight: "4px",
                  }}
                >
                  Close
                </p>
                <img src={close} alt="Close icon." />
              </Button>
            </div>
            <h3
              style={{
                margin: 0,
              }}
            >
              Graph controls
            </h3>
          </div>
          <p style={{ marginBottom: "4px", lineHeight: "1.5" }}>
            Drag the sliders to generate approximate estimates of the number of
            PCR product copies for either total DNA, or just the desired
            amplification region, or both.
          </p>
          {!props.inline ? (
            <Button
              type="Tertiary"
              onClick={() => widget.setDisplayIntro(true)}
            >
              Launch intro
            </Button>
          ) : null}
          <Card
            heading={"Data pop-ups"}
            action={() => widget.setShowTooltips(!widget.showTooltips)}
          ></Card>
          <Card heading={"Number of cycles: " + widget.cycleCounter}>
            <p
              style={{
                margin: "0 0 16px",
                padding: 0,
                color: Colours.charcoal1,
              }}
            >
              Typically, a PCR protocol will repeat ~30 cycles of denaturation,
              annealing, and elongation.
            </p>
            <label style={{ display: "none" }} for="numberOfCycles">
              Number of cycles: <strong>{widget.cycleCounterStop}</strong>
            </label>
            <RangeSlider
              min="1"
              step="1"
              max="30"
              value={widget.cycleCounterStop}
              onChange={(e) => {
                widget.setCycleCounterStop(parseFloat(e.target.value));
              }}
              id="numberOfCycles"
            />
            {!breakpoints.md && !breakpoints.lg ? (
              <Button type="Card-primary" onClick={widget.playCount}>
                {!widget.countPlaying ? (
                  <>
                    <img
                      src={play_icon}
                      alt="Play icon"
                      style={{ marginRight: "8px", height: "12px" }}
                    />
                    <span>Run cycle</span>
                  </>
                ) : (
                  <>
                    <img
                      src={pause_icon}
                      alt="Pause icon"
                      style={{ marginRight: "8px", height: "12px" }}
                    />
                    <span>Pause cycle</span>
                  </>
                )}
              </Button>
            ) : null}
          </Card>
          <Card
            heading={"Process efficiency: " + widget.efficiencyCounter + "%"}
          >
            <p
              style={{
                margin: "0 0 16px",
                padding: 0,
                color: Colours.charcoal1,
              }}
            >
              Using 100 % efficiency provides the theoretical maximum of PCR
              products, but PCR typically only runs at about 80 % efficiency.
            </p>
            <label style={{ display: "none" }} for="efficiency">
              Process efficiency: <strong> {widget.efficiencyCounter}</strong>
            </label>
            <RangeSlider
              min="60"
              step="10"
              max="100"
              value={widget.efficiencyCounter}
              onChange={(e) => {
                widget.setEfficiencyCounter(parseFloat(e.target.value));
              }}
              id="processEfficiency"
            />
          </Card>
          <Card heading="Graph design" expanded="true">
            <div>
              <p
                style={{
                  fontWeight: "bold",
                  marginTop: 0,
                  color: Colours.charcoal1,
                  fontSize: "16px",
                }}
              >
                Lines to plot
              </p>
              <div>
                <RadioButton
                  value="totalDNAline"
                  checked={widget.totalDNAline}
                  onChange={(e) => {
                    updateChartType(e);
                  }}
                  legend={!breakpoints.sm ? total_legend : null}
                  title="Total DNA (Yellow)"
                />
              </div>
              <div>
                <RadioButton
                  value="desiredRegionLine"
                  checked={widget.desiredRegionLine}
                  onChange={(e) => {
                    updateChartType(e);
                  }}
                  legend={!breakpoints.sm ? desired_legend : null}
                  title="Desired Region (Red)"
                />
              </div>
              <div>
                <RadioButton
                  value="overhangLine"
                  checked={widget.overhangLine}
                  onChange={(e) => {
                    updateChartType(e);
                  }}
                  legend={!breakpoints.sm ? overhang_legend : null}
                  title="Overhang Products (Blue)"
                />
              </div>
            </div>
            <div>
              <p
                style={{
                  fontWeight: "bold",
                  color: Colours.charcoal1,
                  fontSize: "16px",
                }}
              >
                Fluid Axes (default is fixed)
              </p>
              <div>
                <CheckBox
                  value="fluidXAxis"
                  checked={widget.fluidXAxis}
                  onChange={(e) => {
                    updateAxis(e);
                  }}
                  title="Fluid X axes"
                />
              </div>
              <div>
                <CheckBox
                  value="fluidYAxis"
                  checked={widget.fluidYAxis}
                  onChange={(e) => {
                    updateAxis(e);
                  }}
                  title="Fluid Y axes"
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
WidgetControls.PropTypes = {
  inline: PropTypes.boolean,
};
